/*
 * BatchConfirmCard — Slice 4 sharp-fill ingest review.
 *
 * After a URL crawl / doc upload / paste / voice transcript drops 2+
 * ai_guessed deltas into the dossier, Orbit renders ONE card listing
 * every just-extracted row instead of asking N Inquirer turns to
 * confirm them one at a time. The user keeps/edits/skips per row and
 * confirms the whole batch in one tap.
 *
 * Brand:
 *   - card surface #eef3ff, 1px brand ring, no shadow (per the AAI kit)
 *   - rows in IBM Plex Mono key / Plex Sans value
 *   - primary CTA "Looks right" in #18191d; per-row edit / skip
 *     icons. No emoji, sentence case, periods.
 *
 * State:
 *   Each row carries a local action — confirm (default), edit (pencil),
 *   skip (✕). Editing opens an inline input scoped to the field's kind.
 *   Submit fires POST /api/intake/confirm-batch with all rows' actions.
 *
 * Returns to the parent the BatchConfirmResponse via onSubmitted so the
 * chat handler can push the resulting next_question into the thread.
 */

import React, { useMemo, useState } from 'react';
import { Check, Pencil, X, Globe } from 'lucide-react';

import type {
  BatchConfirmAction,
  BatchConfirmPayload,
  BatchConfirmResponse,
  BatchConfirmRow,
} from '../../lib/dossier-types';
import { confirmBatch } from '../../lib/intakeApi';


type RowAction = 'confirm' | 'edit' | 'skip';

interface RowState {
  action: RowAction;
  editValue: string;
}


export interface BatchConfirmCardProps {
  payload: BatchConfirmPayload;
  clientId?: string;
  layer?: 'L1' | 'L2';
  onSubmitted: (result: BatchConfirmResponse) => void;
  onDismissed?: () => void;
  disabled?: boolean;
}


export const BatchConfirmCard: React.FC<BatchConfirmCardProps> = ({
  payload,
  clientId,
  layer,
  onSubmitted,
  onDismissed,
  disabled,
}) => {
  const [rowStates, setRowStates] = useState<Record<string, RowState>>(() => {
    const init: Record<string, RowState> = {};
    for (const r of payload.rows) {
      init[r.field] = { action: 'confirm', editValue: r.value_display };
    }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSetAction = (field: string, action: RowAction) => {
    setRowStates(prev => ({
      ...prev,
      [field]: { ...(prev[field] ?? { editValue: '' }), action },
    }));
  };

  const handleEditValue = (field: string, value: string) => {
    setRowStates(prev => ({
      ...prev,
      [field]: { ...(prev[field] ?? { action: 'edit' }), editValue: value, action: 'edit' },
    }));
  };

  const submit = async () => {
    if (submitting || done) return;
    setSubmitError(null);
    const confirmations: BatchConfirmAction[] = payload.rows.map(r => {
      const st = rowStates[r.field] ?? { action: 'confirm', editValue: r.value_display };
      if (st.action === 'edit') {
        return { field: r.field, action: 'edit', value: parseEditValue(r, st.editValue) };
      }
      return { field: r.field, action: st.action };
    });
    setSubmitting(true);
    try {
      const res = await confirmBatch(confirmations, clientId, layer);
      setDone(true);
      onSubmitted(res);
    } catch (e) {
      console.error('[BatchConfirmCard] submit failed', e);
      setSubmitError('Something went wrong saving. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  const keepCount = useMemo(() => {
    let n = 0;
    for (const r of payload.rows) {
      const a = rowStates[r.field]?.action ?? 'confirm';
      if (a !== 'skip') n += 1;
    }
    return n;
  }, [rowStates, payload.rows]);

  // Slice 4 polish — when this card came from email-domain inference
  // AND contains an `a.website` row the user kept, confirming should
  // also trigger a Firecrawl read of that site. We surface this in the
  // primary CTA copy so the click feels intentional ("Looks right and
  // read it") rather than a hidden side effect. The parent does the
  // actual ingest in onSubmitted by inspecting `payload.source`.
  const websiteRowKept = useMemo(() => {
    if (payload.source !== 'email_domain') return null;
    const row = payload.rows.find(r => r.field === 'a.website');
    if (!row) return null;
    const action = rowStates[row.field]?.action ?? 'confirm';
    if (action === 'skip') return null;
    // Pull the host for the CTA copy ("read brndsh.io").
    try {
      const host = new URL(String(row.value)).host;
      return host || null;
    } catch {
      return null;
    }
  }, [payload.source, payload.rows, rowStates]);

  // The email-domain "Is this your website?" card is the user's FIRST
  // touch after logging in with a work email — it deserves a hero render,
  // not the generic field-grid. One domain, confirm or fix, on-brand.
  const isWebsiteConfirm =
    payload.source === 'email_domain'
    && payload.rows.length === 1
    && payload.rows[0].field === 'a.website';

  if (done) {
    // When a website read is firing (user kept a.website on an email-
    // prefill card), the parent kicks off a background crawl. Show a
    // live "researching" state here instead of a dead-end "Saved N
    // fields" line — the click promised "read brndsh.io", so the UI
    // must reflect that it's happening, not collapse to a stale receipt.
    if (websiteRowKept) {
      return (
        <div className="aa-batch-confirm aa-batch-confirm--reading">
          <span className="aa-batch-confirm-reading-dot" />
          <span className="aa-batch-confirm-reading-dot" />
          <span className="aa-batch-confirm-reading-dot" />
          <p className="aa-batch-confirm-reading-line">
            Reading {websiteRowKept} — pulling what I can into your dossier.
          </p>
        </div>
      );
    }
    return (
      <div className="aa-batch-confirm aa-batch-confirm--done">
        <p className="aa-batch-confirm-done-line">
          Saved {keepCount} field{keepCount !== 1 ? 's' : ''} from {payload.source_label}.
        </p>
      </div>
    );
  }

  if (isWebsiteConfirm) {
    const row = payload.rows[0];
    const st = rowStates[row.field] ?? { action: 'confirm' as RowAction, editValue: row.value_display };
    const editing = st.action === 'edit';
    const host = websiteRowKept || row.value_display;
    return (
      <div className="aa-batch-confirm aa-batch-confirm--website">
        <header className="aa-bc-web-header">
          <span className="aa-bc-web-glyph" aria-hidden="true">
            <Globe size={18} strokeWidth={2.25} />
          </span>
          <div className="aa-bc-web-head-text">
            <span className="aa-batch-confirm-eyebrow">From your email</span>
            <h3 className="aa-batch-confirm-title">Is this your website?</h3>
          </div>
        </header>
        <p className="aa-bc-web-sub">
          I'll read it to pre-fill your company profile — only the site you approve.
        </p>

        <div className="aa-bc-web-domain">
          {editing ? (
            <input
              type="text"
              className="aa-batch-confirm-row-input"
              value={st.editValue}
              onChange={e => handleEditValue(row.field, e.target.value)}
              disabled={submitting}
              aria-label="Your website"
              autoFocus
            />
          ) : (
            <span className="aa-bc-web-host" title={row.value_display}>{row.value_display}</span>
          )}
          <button
            type="button"
            className={'aa-batch-confirm-row-btn' + (editing ? ' aa-batch-confirm-row-btn--on' : '')}
            onClick={() => handleSetAction(row.field, editing ? 'confirm' : 'edit')}
            disabled={submitting}
            aria-label={editing ? 'Done editing' : 'Edit website'}
            title={editing ? 'Done' : 'Edit'}
          >
            {editing ? <Check size={13} strokeWidth={2.5} /> : <Pencil size={13} strokeWidth={2.25} />}
          </button>
        </div>

        {submitError && <p className="aa-batch-confirm-error">{submitError}</p>}

        <footer className="aa-batch-confirm-footer">
          <button
            type="button"
            className="aa-batch-confirm-primary"
            onClick={submit}
            disabled={submitting || disabled}
          >
            <Check size={13} strokeWidth={2.5} />
            <span>{editing ? 'Read this site' : `Looks right — read ${host}`}</span>
          </button>
          {!editing && (
            <button
              type="button"
              className="aa-batch-confirm-secondary"
              onClick={() => handleSetAction(row.field, 'edit')}
              disabled={submitting}
            >
              Not my site
            </button>
          )}
        </footer>
      </div>
    );
  }

  return (
    <div className="aa-batch-confirm">
      <header className="aa-batch-confirm-header">
        <span className="aa-batch-confirm-eyebrow">From {payload.source_label}</span>
        {payload.title ? (
          <h3 className="aa-batch-confirm-title">{payload.title}</h3>
        ) : (
          <h3 className="aa-batch-confirm-title">
            I pulled{' '}
            <span className="aa-batch-confirm-title-count">
              {payload.rows.length} thing{payload.rows.length !== 1 ? 's' : ''}
            </span>
            . Look right?
          </h3>
        )}
      </header>

      <ul className="aa-batch-confirm-rows" role="list">
        {payload.rows.map(row => {
          const state = rowStates[row.field] ?? { action: 'confirm' as RowAction, editValue: row.value_display };
          const action = state.action;
          return (
            <li
              key={row.field}
              className={
                'aa-batch-confirm-row'
                + (action === 'skip' ? ' aa-batch-confirm-row--skipped' : '')
                + (action === 'edit' ? ' aa-batch-confirm-row--editing' : '')
              }
            >
              {/* Polished 4-col grid: badge / label / value / actions —
                  each is a direct grid child so widths align cleanly. */}
              <span
                className="aa-batch-confirm-row-section"
                aria-label={`Section ${row.section.toUpperCase()}`}
              >
                {row.section.toUpperCase()}
              </span>
              <span className="aa-batch-confirm-row-name">{row.label}</span>
              <div className="aa-batch-confirm-row-value">
                {action === 'edit' ? (
                  <input
                    type="text"
                    className="aa-batch-confirm-row-input"
                    value={state.editValue}
                    onChange={e => handleEditValue(row.field, e.target.value)}
                    disabled={submitting}
                    aria-label={`New value for ${row.label}`}
                    autoFocus
                  />
                ) : (
                  <span className={action === 'skip' ? 'aa-batch-confirm-row-strike' : ''}>
                    {row.value_display}
                  </span>
                )}
              </div>
              <div className="aa-batch-confirm-row-actions">
                <button
                  type="button"
                  className={
                    'aa-batch-confirm-row-btn'
                    + (action === 'edit' ? ' aa-batch-confirm-row-btn--on' : '')
                  }
                  onClick={() => handleSetAction(row.field, action === 'edit' ? 'confirm' : 'edit')}
                  disabled={submitting}
                  aria-label={`Edit ${row.label}`}
                  title="Edit"
                >
                  <Pencil size={12} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  className={
                    'aa-batch-confirm-row-btn'
                    + (action === 'skip' ? ' aa-batch-confirm-row-btn--on' : '')
                  }
                  onClick={() => handleSetAction(row.field, action === 'skip' ? 'confirm' : 'skip')}
                  disabled={submitting}
                  aria-label={`Skip ${row.label}`}
                  title="Skip"
                >
                  <X size={12} strokeWidth={2.25} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {submitError && (
        <p className="aa-batch-confirm-error">{submitError}</p>
      )}

      <footer className="aa-batch-confirm-footer">
        <button
          type="button"
          className="aa-batch-confirm-primary"
          onClick={submit}
          disabled={submitting || disabled || keepCount === 0}
        >
          <Check size={13} strokeWidth={2.5} />
          <span>
            {keepCount === 0
              ? 'Pick at least one'
              : keepCount === payload.rows.length
                ? (websiteRowKept
                    ? `Looks right — read ${websiteRowKept}`
                    : 'Looks right')
                : (websiteRowKept
                    ? `Save ${keepCount} and read ${websiteRowKept}`
                    : `Save ${keepCount} of ${payload.rows.length}`)}
          </span>
        </button>
        {onDismissed && (
          <button
            type="button"
            className="aa-batch-confirm-secondary"
            onClick={onDismissed}
            disabled={submitting}
          >
            Review later
          </button>
        )}
      </footer>
    </div>
  );
};


/** Best-effort coercion of the user's typed edit back into the field's
 *  registry kind. Multiselect strings split on commas; numbers parsed;
 *  everything else stays a string and the backend's validator handles
 *  the final shape check. */
function parseEditValue(row: BatchConfirmRow, raw: string): unknown {
  const s = (raw ?? '').trim();
  if (!s) return null;
  if (Array.isArray(row.value)) {
    // Multiselect — split on commas + drop empties.
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }
  if (typeof row.value === 'number') {
    const n = Number(s);
    return Number.isFinite(n) ? n : s;
  }
  return s;
}

export default BatchConfirmCard;
